import React from "react";
import { ModalCompAdvance } from "../../../../components";
import {
  formatMoney,
  convertToTitleCase,
  NAIRA_SIGN,
  getTransactionSignedAmountForDisplay,
} from "../../../../utils";
import moment from "moment";
import { AiOutlineClose } from "react-icons/ai";
import { useGetTransactionDetailsByIdQuery } from "../../../../store/admin/transactionSlice";

const StaffTransactionDetailsModal = ({ transactionId, onClose }) => {
  const {
    data: transactionData,
    isLoading,
    isError,
  } = useGetTransactionDetailsByIdQuery(transactionId, {
    skip: !transactionId,
  });

  if (isLoading) {
    return (
      <ModalCompAdvance
        show={true}
        handleClose={onClose}
        size="lg"
        centered
        close={true}
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="p-4 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading transaction details...</p>
              </div>
            ),
          },
        ]}
      />
    );
  }

  let transaction = transactionData;
  if (Array.isArray(transactionData) && transactionData.length > 0) {
    transaction = transactionData[0];
  }

  if (isError || !transaction || !transaction.id) {
    return (
      <ModalCompAdvance
        show={true}
        handleClose={onClose}
        size="lg"
        centered
        close={true}
        modalTitle={
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="fw-bold mb-0">Transaction Details</h5>
            <AiOutlineClose
              onClick={onClose}
              className="cursor-pointer"
              size={24}
              style={{ color: "var(--color-primary)" }}
            />
          </div>
        }
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="text-center py-5">
                <p className="text-danger">Unable to load transaction details.</p>
              </div>
            ),
          },
        ]}
      />
    );
  }

  const getStatusColor = (status) => {
    const statusUpper = (status || "").toUpperCase();
    if (statusUpper === "SUCCESSFUL" || statusUpper === "PROCESSED" || statusUpper === "PAID") return "#19BC31";
    if (statusUpper === "PENDING") return "#FFA500";
    if (statusUpper === "FAILED" || statusUpper === "DECLINED") return "#FF4343";
    return "#A1A0A0";
  };

  const getTransactionTypeName = (transaction) => {
    return transaction?.display_type || convertToTitleCase(transaction?.type) || "Transaction";
  };

  const getPaymentMethodName = (transaction) => {
    return transaction?.display_payment_method || transaction?.payment_method || "N/A";
  };

  const statusColor = getStatusColor(transaction.status);
  const transactionType = getTransactionTypeName(transaction);
  const safeAmount = getTransactionSignedAmountForDisplay(transaction);
  const amountColor =
    safeAmount < 0 ? "#d40000" : safeAmount > 0 ? "#19BC31" : "var(--color-primary)";
  const flowLabel =
    safeAmount < 0 ? "Money out (debit)" : safeAmount > 0 ? "Money in (credit)" : null;

  return (
    <ModalCompAdvance
      show={true}
      handleClose={onClose}
      size="lg"
      centered
      close={true}
      modalTitle={
        <div className="d-flex justify-content-between align-items-center w-100">
          <h4 className="fw-bold mb-0" style={{ color: "var(--color-primary)" }}>
            Transaction Details
          </h4>
          <AiOutlineClose
            onClick={onClose}
            className="cursor-pointer"
            size={24}
            style={{ color: "var(--color-primary)" }}
          />
        </div>
      }
      modalBody={0}
      arrayComp={[
        {
          component: (
            <div className="p-4">
              <div className="row g-4">
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Member Name</p>
                  <p className="fw-bold text-black1 mb-0">
                    {typeof transaction?.member === 'string' 
                      ? transaction.member 
                      : (transaction?.member?.first_name 
                          ? `${transaction.member.first_name} ${transaction.member.last_name || ""}` 
                          : String(transaction?.member?.name || transaction?.member || "N/A"))}
                  </p>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Membership No</p>
                  <p className="fw-bold text-black1 mb-0">
                    {String(transaction?.member_no || transaction?.member?.member_no || "N/A")}
                  </p>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Transaction Type</p>
                  <p className="fw-bold text-black1 mb-0">{transactionType}</p>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Amount</p>
                  <p className="fs-5 fw-bold mb-0" style={{ color: amountColor }}>
                    {formatMoney(safeAmount.toFixed(2), true, NAIRA_SIGN)}
                  </p>
                  {flowLabel ? (
                    <p className="small text-muted mb-0 mt-1">{flowLabel}</p>
                  ) : null}
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Reference</p>
                  <p className="small fw-bold text-black1 mb-0" style={{ fontFamily: "monospace" }}>
                    {transaction?.reference || "N/A"}
                  </p>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Payment Method</p>
                  <p className="small fw-bold text-black1 mb-0">{getPaymentMethodName(transaction)}</p>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Status</p>
                  <span
                    className="rounded-pill px-3 py-1 text-white"
                    style={{ backgroundColor: statusColor, fontSize: "12px" }}
                  >
                    {convertToTitleCase(transaction?.status || "UNKNOWN")}
                  </span>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Date & Time</p>
                  <p className="small fw-bold text-black1 mb-0">
                    {moment(transaction?.created_at).format("DD MMMM, YYYY - h:mm A")}
                  </p>
                </div>
                {transaction?.remark && (
                  <div className="col-12 border-bottom pb-3">
                    <p className="small text-muted mb-1">Remark/Description</p>
                    <p className="small fw-semibold text-black1 mb-0">{transaction.remark}</p>
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end mt-4">
                <button
                  className="btn px-4 py-2 text-white rounded"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          ),
        },
      ]}
    />
  );
};

export default StaffTransactionDetailsModal;

